import { renderAsync } from "docx-preview";
import { action, makeObservable, observable } from "mobx";
import { createRef } from "react";

export type DocxPreviewState = "loading" | "ready" | "error";

export class DocxPreviewStore {
  containerRef = createRef<HTMLDivElement>();

  @observable.ref
  state: DocxPreviewState = "loading";

  @observable.ref
  private url: string | null = null;

  private requestId = 0;

  constructor() {
    makeObservable(this);
  }

  @action.bound
  setUrl(url: string): void {
    this.url = url;
  }

  @action.bound
  async load(): Promise<void> {
    const requestId = ++this.requestId;
    const container = this.containerRef.current;

    if (!container || !this.url) {
      this.setState("error");
      return;
    }

    this.setState("loading");
    this.clearContainer();

    try {
      const response = await fetch(this.url, { credentials: "include" });

      if (!response.ok) {
        throw new Error(`Failed to fetch document (${response.status})`);
      }

      const buffer = await response.arrayBuffer();

      if (requestId !== this.requestId || !this.containerRef.current) {
        return;
      }

      await renderAsync(buffer, this.containerRef.current, undefined, {
        className: "docx-preview",
        inWrapper: true,
        breakPages: true,
        ignoreWidth: false,
        ignoreHeight: false,
        useBase64URL: true,
      });

      if (requestId !== this.requestId) {
        return;
      }

      this.setState("ready");
    } catch {
      if (requestId !== this.requestId) {
        return;
      }

      this.setState("error");
    }
  }

  @action.bound
  dispose(): void {
    this.requestId += 1;
    this.url = null;
    this.clearContainer();
  }

  @action.bound
  private setState(state: DocxPreviewState): void {
    this.state = state;
  }

  private clearContainer(): void {
    if (this.containerRef.current) {
      this.containerRef.current.innerHTML = "";
    }
  }
}
